import { env, pipeline } from "@huggingface/transformers";
import type { FeatureExtractionPipeline, ProgressInfo } from "@huggingface/transformers";
import { log } from "../../logger";
import { embeddingModelCache } from "../../shared/lib/embeddingModelCache";
import { EMBEDDINGGEMMA_MODEL_DIR } from "../../shared/lib/modelManifest";

export const EMBEDDING_DIMS = 768;

/**
 * Weight precision requested from transformers.js (ticket 07). Must be `"q8"`
 * (dynamic-quantized `onnx/model_quantized.onnx`): the `"q4"` blockwise
 * variant's quantized embedding node (`GatherBlockQuantized`) has no kernel in
 * the onnxruntime WASM build, so session creation throws `ERROR_CODE: 9` and
 * every free-text search degrades to keyword fallback. The upstream model card
 * supports `"fp32" | "q8" | "q4"` (fp16 and derivatives excluded), and the
 * transformers.js wasm default is itself `"q8"` — this constant pins that
 * default explicitly so a library upgrade can't silently re-select `"q4"`.
 * Exported for unit testing.
 */
export const EMBEDDING_DTYPE = "q8";

/** Same-origin directory of the vendored onnxruntime-web WASM backend (ticket 05). */
export const ORT_WASM_PATH_PREFIX = "/ort/";

/** Model-load progress as a 0..1 fraction (mirrors the gallery's UForm progress). */
export type EmbeddingProgressListener = (fraction: number) => void;

/**
 * Route transformers.js model I/O through the Browser model store (IndexedDB)
 * and lock model loading to same-origin files: a warm store serves every
 * model file with no `/model/...` network fetch, a cold miss fetches
 * same-origin once and persists the bytes with the baked version key, and a
 * public CDN is never consulted at runtime. Idempotent — safe to call per
 * load. Never throws (guards against a missing `env`).
 *
 * `allowLocalModels` must be set explicitly: transformers v4 defaults it to
 * `false` in browsers, so `allowRemoteModels = false` alone disables both
 * sources and every `pipeline()` call throws "both local and remote models
 * are disabled". Exported for unit testing; also called lazily per load.
 *
 * The onnxruntime WASM backend locator (`wasmPaths`) is pointed at the
 * vendored same-origin `/ort/` dir (see `scripts/vendor-ort.mjs`): without
 * this, transformers.js falls back to its baked public-CDN prefix and the
 * first inference attempt dynamically imports the `.mjs` proxy from
 * `cdn.jsdelivr.net`, which fails self-hosted/offline with
 * `no available backend found`. A string prefix (rather than a pinned
 * `{ mjs, wasm }` pair) lets the runtime pick the right file per browser
 * (Safari vs asyncify). Set before the first `pipeline()` call — it
 * overwrites the CDN default transformers.js installs at import time.
 */
export function configureModelRuntime(): void {
  try {
    if (!env) return;
    env.allowRemoteModels = false;
    env.allowLocalModels = true;
    env.useCustomCache = true;
    env.customCache = embeddingModelCache;
    const onnx = env.backends?.onnx as { wasm?: { wasmPaths?: string | object } } | undefined;
    if (onnx) {
      onnx.wasm ??= {};
      onnx.wasm.wasmPaths = ORT_WASM_PATH_PREFIX;
    }
  } catch (err) {
    log.warn({ err }, "[semantic-search] could not configure the browser model store");
  }
}

/**
 * EmbeddingGemma document/query prompt prefixes (Gemma embedding convention).
 * Documents are embedded for ingestion; queries are embedded for retrieval.
 */
const DOCUMENT_PREFIX = "search document: ";
const QUERY_PREFIX = "search query: ";

/**
 * Aggregate transformers.js per-file `progress` events into a running
 * `loaded / total` fraction across all model files. Pure: feed it the
 * `progress_callback` payloads and it emits the fraction. Only
 * `progress`-status events carry byte counts — `initiate`/`download`/`done`
 * statuses are ignored. On a warm store hit the same events fire while
 * reading bytes back from IndexedDB, so the fraction there reflects a real
 * (fast) store read, never a faked download.
 */
export function createEmbeddingProgressAggregator(
  emit: EmbeddingProgressListener,
): (event: ProgressInfo) => void {
  let loaded = 0;
  let total = 0;
  const files = new Map<string, { loaded: number; total: number }>();
  return (event) => {
    if (event.status !== "progress") return;
    const prev = files.get(event.file) ?? { loaded: 0, total: 0 };
    loaded += event.loaded - prev.loaded;
    if (event.total > 0 && event.total !== prev.total) {
      total += event.total - prev.total;
    }
    files.set(event.file, { loaded: event.loaded, total: event.total });
    emit(total > 0 ? Math.min(1, loaded / total) : 0);
  };
}

/**
 * Thin wrapper around `@huggingface/transformers` running EmbeddingGemma
 * (300M, q8) on WASM. The model is bundled in `public/model/` (ADR-0022, A3);
 * the Browser model store (IndexedDB) persists its bytes after one
 * same-origin download, so repeat visits load with no `/model/...` fetch.
 *
 * `ready` is a promise that never rejects: if the model fails to load (missing
 * files, browser too old, WASM blocked) it resolves `false`, and the search
 * router degrades to the Orama keyword fallback (ADR-0022, B4).
 */
export class EmbeddingService {
  #pipelinePromise: Promise<FeatureExtractionPipeline | null> | null = null;
  #readyPromise: Promise<boolean> | null = null;
  #onProgress?: EmbeddingProgressListener;

  constructor(private readonly modelPath = EMBEDDINGGEMMA_MODEL_DIR) {}

  /** Register (or clear) the model-load progress listener. */
  onProgress(listener: EmbeddingProgressListener | undefined): void {
    this.#onProgress = listener;
  }

  /**
   * Resolves true once the model is loaded. Lazy: the first access triggers the
   * (single) load attempt, so importing the service has no side effects until
   * the app actually searches.
   */
  get ready(): Promise<boolean> {
    if (!this.#readyPromise) {
      this.#readyPromise = this.#init().then((ok) => {
        if (!ok) {
          this.#readyPromise = null;
          this.#pipelinePromise = null;
        }
        return ok;
      });
    }
    return this.#readyPromise;
  }

  async #init(): Promise<boolean> {
    try {
      const p = await this.loadPipeline();
      return p !== null;
    } catch (err) {
      log.warn({ err }, "[semantic-search] EmbeddingGemma failed to load; falling back to keyword search");
      return false;
    }
  }

  private loadPipeline(): Promise<FeatureExtractionPipeline | null> {
    if (!this.#pipelinePromise) {
      configureModelRuntime();
      const listener = this.#onProgress;
      const aggregate = listener ? createEmbeddingProgressAggregator(listener) : undefined;
      this.#pipelinePromise = pipeline("feature-extraction", this.modelPath, {
        // q8 matches the bundled ONNX weights (ADR-0022, A3): the EmbeddingGemma
        // ONNX export ships `onnx/model_quantized.onnx` + external `onnx_data`
        // (see EMBEDDING_DTYPE — q4 is not WASM-runnable).
        dtype: EMBEDDING_DTYPE,
        device: "wasm",
        ...(aggregate ? { progress_callback: aggregate } : {}),
      }).catch((err) => {
        this.#pipelinePromise = null;
        log.warn({ err }, "[semantic-search] EmbeddingGemma load error");
        return null;
      });
    }
    return this.#pipelinePromise;
  }

  /** Embed text and return the pooled, normalized 768-dim vector. */
  async embed(text: string): Promise<Float32Array> {
    const pipe = await this.loadPipeline();
    if (!pipe) {
      throw new Error("embedding model is unavailable");
    }
    const out = await pipe(DOCUMENT_PREFIX + text, { pooling: "mean", normalize: true });
    const data = out.data as Float32Array;
    const dims = out.dims;
    const size = dims[dims.length - 1] ?? EMBEDDING_DIMS;
    const vec = new Float32Array(size);
    vec.set(data.subarray(0, size));
    return vec;
  }

  /** Embed a free-text query (uses the `search query` prefix). */
  async embedQuery(query: string): Promise<Float32Array> {
    const pipe = await this.loadPipeline();
    if (!pipe) {
      throw new Error("embedding model is unavailable");
    }
    const out = await pipe(QUERY_PREFIX + query, { pooling: "mean", normalize: true });
    const data = out.data as Float32Array;
    const dims = out.dims;
    const size = dims[dims.length - 1] ?? EMBEDDING_DIMS;
    const vec = new Float32Array(size);
    vec.set(data.subarray(0, size));
    return vec;
  }
}

/** Shared lazy singleton used across the app. */
export const embeddingService = new EmbeddingService();
